/**
 * What the reception typed in the big "Find customer" box, and how it is looked up with the APIs the live server
 * already has (no new endpoint):
 *
 *   mobile    9876543210, +91 98765 43210, 09876543210      → GET /customers?q=+919876543210 (exact number first)
 *   digits    4–9 digits (part of a mobile or a plate number) → GET /customers?q=…            (the server tries both)
 *   plate     KL 11 AB 1234, kl-07-bx-5521, 22 BH 1234 AA, AB1234 → GET /customers?q=KL11AB1234
 *   name      Ravi, Anjali Nair                               → GET /customers?q=…
 *   text      anything else long enough (e.g. "ravi 98")      → GET /customers?q=…
 *   card      a 16-digit wallet card number (Luhn)            → POST /wallet/reception/lookup { customerQr }
 *   qr        the QR in the customer's app (scanned or pasted) → POST /wallet/reception/lookup { customerQr }
 *
 * Pure functions only, so the rules are unit tested (web/src/pages/staff/reception/home/__tests__/search.test.ts).
 */

export type SearchKind = 'empty' | 'short' | 'mobile' | 'digits' | 'plate' | 'name' | 'text' | 'card' | 'qr';

export interface ParsedSearch {
  kind: SearchKind;
  /** The text as typed, trimmed. */
  text: string;
  /** E.164 (+919876543210), for 'mobile'. */
  mobile?: string;
  /** The plate without spaces (KL11AB1234), for 'plate'. */
  plate?: string;
  /** The plate as written on the vehicle (KL 11 AB 1234), for 'plate'. */
  plateDisplay?: string;
  /** A whole plate (KL 11 AB 1234) rather than part of one (AB1234). */
  fullPlate?: boolean;
  /** The 16 digits, for 'card'. */
  card?: string;
  /** What to send as customerQr, for 'qr'. */
  token?: string;
}

/** The same rule as the server (server/src/lib/mobile.ts): Indian mobiles to +91XXXXXXXXXX, other "+…" numbers kept. */
export function normalizeMobile(input: string | null | undefined): string | null {
  if (!input) return null;
  const trimmed = String(input).trim();
  let digits = trimmed.replace(/\D/g, '');
  if (trimmed.startsWith('+') && !digits.startsWith('91')) return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
  else if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
  if (!/^[6-9]\d{9}$/.test(digits)) return null;
  return `+91${digits}`;
}

/** "+919876543210" → "98765 43210" (other numbers as they are). */
export function mobileDisplay(e164: string | null | undefined): string {
  if (!e164) return '—';
  return e164.startsWith('+91') && e164.length === 13 ? `${e164.slice(3, 8)} ${e164.slice(8)}` : e164;
}

/** A wallet card number: 16 digits passing the Luhn check (the server's rule). */
export function isCardNumber(text: string): boolean {
  const d = text.replace(/[\s-]/g, '');
  if (!/^\d{16}$/.test(d)) return false;
  let sum = 0;
  for (let i = d.length - 1, dbl = false; i >= 0; i--, dbl = !dbl) {
    let x = Number(d[i]);
    if (dbl) {
      x *= 2;
      if (x > 9) x -= 9;
    }
    sum += x;
  }
  return sum % 10 === 0;
}

/** Indian plates, as the server reads them (server/src/lib/regno.ts). */
const STANDARD = /^([A-Z]{2})(\d{1,2})([A-Z]{0,3})(\d{1,4})$/;
const BHARAT = /^(\d{2})(BH)(\d{4})([A-Z]{1,2})$/;
/** Part of a plate: "AB1234", "KL11", "11AB1234", "KL11AB". */
const PARTIAL = [/^[A-Z]{1,3}\d{1,4}$/, /^\d{1,2}[A-Z]{1,3}\d{1,4}$/, /^[A-Z]{2}\d{1,2}[A-Z]{1,3}$/];

export const compactPlate = (text: string) => text.toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * A plate as people write it: "kl-7-ab-1234" → { compact: "KL07AB1234", display: "KL 07 AB 1234", full: true }.
 * A whole plate has its series letters ("KL 7 AB 1234") or a 4-digit number after a 2-digit RTO ("KL 01 1234");
 * anything shorter ("AB1234", "KL11") is part of one and is kept as typed, so the search looks for it inside plates.
 */
export function readPlate(text: string): { compact: string; display: string; full: boolean } | null {
  const c = compactPlate(text);
  if (c.length < 4 || c.length > 15) return null;
  const bh = BHARAT.exec(c);
  if (bh) return { compact: c, display: `${bh[1]} BH ${bh[3]} ${bh[4]}`, full: true };
  const std = STANDARD.exec(c);
  if (std) {
    const [, state, rto, series, num] = std;
    if (series.length > 0 || (rto.length === 2 && num.length === 4)) {
      const rto2 = rto.padStart(2, '0');
      return { compact: `${state}${rto2}${series}${num}`, display: [state, rto2, series, num].filter(Boolean).join(' '), full: true };
    }
    return { compact: c, display: c, full: false };
  }
  if (PARTIAL.some((re) => re.test(c))) return { compact: c, display: c, full: false };
  return null;
}

/** The token inside a scanned app QR: the raw token, or a link carrying it as ?t= / ?token= / ?cq= (as the gate reads it). */
export function qrToken(text: string): string {
  const t = text.trim();
  if (/^z3c:/i.test(t)) return t.slice(4);
  try {
    const u = new URL(t);
    return u.searchParams.get('t') ?? u.searchParams.get('token') ?? u.searchParams.get('cq') ?? t;
  } catch {
    return t;
  }
}

const LOOKS_LIKE_LINK = /^(https?:\/\/|z3c:)/i;
/** The app's QR token: base64url text with a signature after a dot, never with spaces. */
const LOOKS_LIKE_TOKEN = /^[A-Za-z0-9_-]{16,}\.[A-Za-z0-9_-]{16,}$/;
/** Only digits and the separators people type in phone numbers. */
const DIGITISH = /^[+\d\s()-]+$/;
/** Letters (any script, with its vowel signs), spaces, dots, apostrophes and hyphens: a name. */
const NAMEISH = /^[\p{L}\p{M}\s.'’-]+$/u;

export function parseSearch(raw: string): ParsedSearch {
  const text = (raw ?? '').trim();
  if (!text) return { kind: 'empty', text };

  if (LOOKS_LIKE_LINK.test(text) || LOOKS_LIKE_TOKEN.test(text)) return { kind: 'qr', text, token: qrToken(text) };

  if (DIGITISH.test(text)) {
    const digits = text.replace(/\D/g, '');
    if (isCardNumber(text)) return { kind: 'card', text, card: digits };
    const mobile = normalizeMobile(text);
    if (mobile) return { kind: 'mobile', text, mobile };
    return digits.length >= 4 ? { kind: 'digits', text } : { kind: 'short', text };
  }

  const letters = text.replace(/[^\p{L}]/gu, '').length;
  if (NAMEISH.test(text)) return letters >= 2 ? { kind: 'name', text } : { kind: 'short', text };

  // Letters and digits together, written like a plate (spaces, dashes and dots allowed between the parts).
  if (/^[A-Za-z0-9\s.-]+$/.test(text) && /\d/.test(text) && /[A-Za-z]/.test(text)) {
    const p = readPlate(text);
    if (p) return { kind: 'plate', text, plate: p.compact, plateDisplay: p.display, fullPlate: p.full };
  }
  return text.length >= 3 ? { kind: 'text', text } : { kind: 'short', text };
}

/** What goes to GET /customers?q=, or null when the list is not the way to find it (too short, a card, a QR). */
export function listQuery(p: ParsedSearch): string | null {
  switch (p.kind) {
    case 'mobile':
      return p.mobile ?? null;
    case 'plate':
      return p.plate ?? null;
    case 'digits':
    case 'name':
    case 'text':
      return p.text;
    default:
      return null;
  }
}

/** How the box understood the text, shown next to it so the reception sees what is being searched. */
export function searchLabel(p: ParsedSearch): string | null {
  switch (p.kind) {
    case 'mobile':
      return `Mobile ${mobileDisplay(p.mobile)}`;
    case 'digits':
      return 'Part of a mobile or vehicle number';
    case 'plate':
      return p.fullPlate ? `Vehicle ${p.plateDisplay}` : 'Part of a vehicle number';
    case 'name':
      return 'Name';
    case 'text':
      return 'Name, mobile or vehicle';
    case 'card':
      return 'Wallet card — press Enter';
    case 'qr':
      return 'App QR — press Enter';
    case 'short':
      return 'Keep typing…';
    default:
      return null;
  }
}

/** What a "New customer" form can be filled in with from the search (a mobile, a plate or a name). */
export function prefillFrom(p: ParsedSearch): { name?: string; mobile?: string; plate?: string } {
  if (p.kind === 'mobile' && p.mobile) return { mobile: p.mobile.startsWith('+91') ? p.mobile.slice(3) : p.mobile };
  if (p.kind === 'plate' && p.fullPlate && p.plateDisplay) return { plate: p.plateDisplay };
  if (p.kind === 'name') return { name: p.text.replace(/\s+/g, ' ').replace(/(^|\s)\p{Ll}/gu, (m) => m.toUpperCase()) };
  return {};
}
